import { roundToStrike } from "@/lib/options/structure";
import type { Mode, Regime } from "@/types/options";
import { defineStrategy } from "./define";
import { leg } from "./leg";
import { offsetParam, toggleParam } from "./params";
import { scale } from "./scale";

// ── Single Call ─────────────────────────────────────────────────────────────
// Side toggle: +1 long, -1 short. Signed strikeOffset (negative = below spot).

export const singleCall = defineStrategy("single_call", "Call", "single", {
  defaultParams: () => ({ side: 1, strikeOffset: 0 }),
  paramSchema: [
    toggleParam("side", "Side", [
      [1, "Long"],
      [-1, "Short"],
    ]),
    offsetParam("strikeOffset", "Strike Offset", -100, 100),
  ],
  scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
    side: cur.side ?? 1,
    strikeOffset: scale(0, r, m, true, 100),
  }),
  toLegs: (spot, params, strikeStep) => {
    const strike = roundToStrike(spot, strikeStep) + params.strikeOffset;
    const s: "long" | "short" = params.side >= 0 ? "long" : "short";
    return [leg(strike, "call", s)];
  },
});

// ── Single Put ──────────────────────────────────────────────────────────────

export const singlePut = defineStrategy("single_put", "Put", "single", {
  defaultParams: () => ({ side: 1, strikeOffset: 0 }),
  paramSchema: [
    toggleParam("side", "Side", [
      [1, "Long"],
      [-1, "Short"],
    ]),
    offsetParam("strikeOffset", "Strike Offset", -100, 100),
  ],
  scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
    side: cur.side ?? 1,
    strikeOffset: scale(0, r, m, true, 100),
  }),
  toLegs: (spot, params, strikeStep) => {
    const strike = roundToStrike(spot, strikeStep) + params.strikeOffset;
    const s: "long" | "short" = params.side >= 0 ? "long" : "short";
    return [leg(strike, "put", s)];
  },
});

// ── Covered Call ───────────────────────────────────────────────────────────

export const coveredCall = defineStrategy(
  "covered_call",
  "Covered Call",
  "covered",
  {
    defaultParams: () => ({ strikeOffset: 20 }),
    paramSchema: [offsetParam("strikeOffset", "Strike Offset", 0, 100)],
    scaleParams: (r: Regime, m: Mode) => ({
      strikeOffset: scale(20, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [leg(center + params.strikeOffset, "call", "short")];
    },
  },
);

// ── Cash Secured Put ──────────────────────────────────────────────────────

export const cashSecuredPut = defineStrategy(
  "cash_secured_put",
  "Cash Secured Put",
  "covered",
  {
    defaultParams: () => ({ strikeOffset: 20 }),
    paramSchema: [offsetParam("strikeOffset", "Strike Offset", 0, 100)],
    scaleParams: (r: Regime, m: Mode) => ({
      strikeOffset: scale(20, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [leg(center - params.strikeOffset, "put", "short")];
    },
  },
);

// ── Bull Call Spread ──────────────────────────────────────────────────────

export const bullCallSpread = defineStrategy(
  "bull_call_spread",
  "Bull Call Spread",
  "vertical",
  {
    defaultParams: () => ({ longOffset: 0, shortOffset: 20 }),
    paramSchema: [
      offsetParam("longOffset", "Long Strike Offset", -50, 50, 0),
      offsetParam("shortOffset", "Short Strike Offset", 5, 100, 1),
    ],
    scaleParams: (r: Regime, m: Mode) => ({
      longOffset: scale(0, r, m, true, 50),
      shortOffset: scale(20, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [
        leg(center + params.longOffset, "call", "long"),
        leg(center + params.shortOffset, "call", "short"),
      ];
    },
  },
);

// ── Bear Put Spread ───────────────────────────────────────────────────────

export const bearPutSpread = defineStrategy(
  "bear_put_spread",
  "Bear Put Spread",
  "vertical",
  {
    defaultParams: () => ({ longOffset: 0, shortOffset: 20 }),
    paramSchema: [
      offsetParam("longOffset", "Long Strike Offset", -50, 50, 0),
      offsetParam("shortOffset", "Short Strike Offset", 5, 100, 1),
    ],
    scaleParams: (r: Regime, m: Mode) => ({
      longOffset: scale(0, r, m, true, 50),
      shortOffset: scale(20, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [
        leg(center - params.longOffset, "put", "long"),
        leg(center - params.shortOffset, "put", "short"),
      ];
    },
  },
);

// ── Bull Put Spread ───────────────────────────────────────────────────────

export const bullPutSpread = defineStrategy(
  "bull_put_spread",
  "Bull Put Spread",
  "vertical",
  {
    defaultParams: () => ({ shortOffset: 20, longOffset: 40 }),
    paramSchema: [
      offsetParam("shortOffset", "Short Strike Offset", 5, 80, 0),
      offsetParam("longOffset", "Long Strike Offset", 10, 120, 1),
    ],
    scaleParams: (r: Regime, m: Mode) => ({
      shortOffset: scale(20, r, m, true),
      longOffset: scale(40, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [
        leg(center - params.shortOffset, "put", "short"),
        leg(center - params.longOffset, "put", "long"),
      ];
    },
  },
);

// ── Bear Call Spread ──────────────────────────────────────────────────────

export const bearCallSpread = defineStrategy(
  "bear_call_spread",
  "Bear Call Spread",
  "vertical",
  {
    defaultParams: () => ({ shortOffset: 20, longOffset: 40 }),
    paramSchema: [
      offsetParam("shortOffset", "Short Strike Offset", 5, 80, 0),
      offsetParam("longOffset", "Long Strike Offset", 10, 120, 1),
    ],
    scaleParams: (r: Regime, m: Mode) => ({
      shortOffset: scale(20, r, m, true),
      longOffset: scale(40, r, m, true),
    }),
    toLegs: (spot, params, strikeStep) => {
      const center = roundToStrike(spot, strikeStep);
      return [
        leg(center + params.shortOffset, "call", "short"),
        leg(center + params.longOffset, "call", "long"),
      ];
    },
  },
);
